import { useCallback, useMemo } from 'react';
import toast from 'react-hot-toast';

import { FormContainer } from '../../../_core/form/form-container.component';
import { DownloadRequestAssetsForm } from './download-request-assets.component';
import { IRequestOverview, IRequestTokenParams } from '../../../../lib/types';
import { ICloseableDialogProps } from '../../../_core/core.types';
import { showError, sleep } from '../../../../lib/utils';
import { useRequestWithAuth, useRequestWithToken } from '../../request/request.service';
import { Loader } from '../../../_core/loader.component';
import { IDownloadRequestAssetsFormParams } from './download-request-assets.types';
import { useDownloadRequestAssets } from '../../request/request-pdf.service';
import { ISectionData } from '../../../_editor/section/section.types';
import { useLazyGetJob } from '../../job/job.service';
import { getName } from '../../../../utils/name.utils';

interface IDownloadRequestAssetsContainer extends Partial<ICloseableDialogProps>, IRequestTokenParams {
  editorData: ISectionData[];
  requestOverview: IRequestOverview;
}

export const DownloadRequestAssetsContainer = ({
  editorData,
  requestOverview,
  requestToken = '',
  close
}: IDownloadRequestAssetsContainer) => {
  const requestId = requestOverview._id;
  const { request: requestWithAuth } = useRequestWithAuth(requestOverview);
  const { requestResponse: requestWithToken } = useRequestWithToken({ requestId, token: requestToken });

  const { downloadRequestAssets, startDownloadRequestAssets } = useDownloadRequestAssets({
    requestId,
    requestToken
  });
  const { getJob } = useLazyGetJob();

  const request = useMemo(() => requestWithAuth ?? requestWithToken?.request, [requestWithAuth, requestWithToken]);

  const downloadJobAssets = useCallback(
    (jobId: string, fileName: string) => {
      const toastId = 'download-' + fileName;
      return getJob({ variables: { _id: jobId } })
        .then((job) => {
          if (job.data?.getJob) {
            if (job.data.getJob.failReason)
              toast.error('Failed to download request assets', { id: toastId, duration: 5000 });
            // If job has yet to finish, then wait and recheck job status in a few seconds
            else if (!job.data.getJob.lastFinishedAt) sleep(2000).then(() => downloadJobAssets(jobId, fileName));
            else if (!job.data.getJob.result)
              toast.error('Failed to download request assets', { id: toastId, duration: 5000 });
            else {
              toast.loading('Beginning request assets...', { id: toastId, duration: 30000 });
              downloadRequestAssets(job.data.getJob.result, fileName);
            }
          } else toast.error('Failed to download request assets', { id: toastId, duration: 5000 });
        })
        .catch(() => toast.error('Failed to download request assets', { id: toastId, duration: 5000 }));
    },
    [downloadRequestAssets, getJob]
  );

  const handleSubmit = useCallback(
    async ({ assets = [], sections = [], requestFileName }: IDownloadRequestAssetsFormParams) => {
      try {
        let fileName = '';
        const isSingleAsset = assets.length + sections.length + (requestFileName ? 1 : 0) === 1;
        if (isSingleAsset) {
          if (assets.length) fileName = assets[0].fileName;
          else if (sections.length) fileName = sections[0].fileName;
          else if (requestFileName) fileName = requestFileName;
        } else fileName = `Request - ${request?.name ?? 'Unknown'} - ${getName(request?.assignedTo) || 'Unknown'}`;

        const job = await startDownloadRequestAssets({ assets, fileName, requestFileName, sections });
        if (job) downloadJobAssets(job, fileName);

        if (close) {
          await sleep(250);
          close();
        }
      } catch (err) {
        showError('Failed sending message', err as Error);
      }
    },
    [request?.name, request?.assignedTo, startDownloadRequestAssets, downloadJobAssets, close]
  );

  return (
    <FormContainer title="Download documents for request" subtitle={request?.name}>
      {request ? (
        <DownloadRequestAssetsForm editorData={editorData} request={request} onBack={close} onSubmit={handleSubmit} />
      ) : (
        <Loader />
      )}
    </FormContainer>
  );
};
